import axios from 'axios';

/**
 * Direct browser → Nova Poshta client, used only for read-only reference lookups (city
 * search, warehouses, sender's own contact persons) that feed the create-shipment form's
 * autocompletes. The waybill itself (InternetDocument/save) and its Counterparty setup
 * moved server-side — see CreateNovaPoshtaDocumentUseCase.java — so a rejected/failed
 * call shows up in our own backend logs instead of only the browser console.
 *
 * Deliberately NOT routed through apiClient (that instance's baseURL/withCredentials
 * point at our own backend) — CORS against api.novaposhta.ua is verified permissive for
 * arbitrary origins (see doc/shipping/novaposhta-integration.md#rate-limits--error-handling),
 * so a bare axios call is enough. Mirrors the envelope shape of be/.../NovaPoshtaClient.java.
 */

const NOVA_POSHTA_URL = 'https://api.novaposhta.ua/v2.0/json/';

interface NovaPoshtaResponse {
  success: boolean;
  data: Array<Record<string, unknown>>;
  errors: string[];
}

async function call(
  apiKey: string,
  modelName: string,
  calledMethod: string,
  methodProperties: Record<string, unknown>,
): Promise<NovaPoshtaResponse> {
  const response = await axios.post<NovaPoshtaResponse>(NOVA_POSHTA_URL, {
    apiKey,
    modelName,
    calledMethod,
    methodProperties,
  });
  return response.data;
}

function firstErrorOrThrow(response: NovaPoshtaResponse, fallbackMessage: string): never {
  throw new Error(response.errors?.[0] ?? fallbackMessage);
}

async function getSenderCounterpartyRef(apiKey: string): Promise<string> {
  const response = await call(apiKey, 'Counterparty', 'getCounterparties', {
    CounterpartyProperty: 'Sender',
    Page: '1',
  });
  if (!response.success || response.data.length === 0) {
    firstErrorOrThrow(response, 'Не вдалося отримати відправника Нової пошти');
  }
  return response.data[0].Ref as string;
}

async function getContactPersons(apiKey: string, counterpartyRef: string): Promise<ContactPersonResult[]> {
  const response = await call(apiKey, 'Counterparty', 'getCounterpartyContactPersons', {
    Ref: counterpartyRef,
    Page: '1',
  });
  if (!response.success) {
    firstErrorOrThrow(response, 'Не вдалося отримати контактних осіб');
  }
  return response.data.map((c) => ({
    ref: c.Ref as string,
    name: c.Description as string,
    phone: (c.Phones as string) ?? '',
  }));
}

export interface CitySearchResult {
  /** DeliveryCity — the Ref that getWarehouses/InternetDocument/save actually expect, not the settlement Ref. */
  ref: string;
  present: string;
}

export interface WarehouseSearchResult {
  ref: string;
  number: string;
  description: string;
  category: string;
}

export interface ContactPersonResult {
  ref: string;
  name: string;
  phone: string;
}

export const novaPoshtaReference = {
  /**
   * AddressGeneral/searchSettlements — verified live (doc/shipping/request-response.md).
   * Response nests results under data[0].Addresses, not a flat data list.
   */
  searchSettlements: async (apiKey: string, query: string): Promise<CitySearchResult[]> => {
    const response = await call(apiKey, 'AddressGeneral', 'searchSettlements', {
      CityName: query,
      Limit: '20',
      Page: '1',
    });
    if (!response.success) {
      firstErrorOrThrow(response, 'Не вдалося знайти населений пункт');
    }
    const addresses = (response.data[0]?.Addresses as Array<Record<string, unknown>>) ?? [];
    return addresses.map((a) => ({ ref: a.DeliveryCity as string, present: a.Present as string }));
  },

  /**
   * Address/getWarehouses — verified live: CityRef must be DeliveryCity (not settlement Ref),
   * see doc/shipping/novaposhta-integration.md#nova-poshta-api. Fetched once per city and
   * filtered client-side (Autocomplete) rather than re-queried per keystroke — NP's rate limit
   * is tight enough to trip on two rapid calls (observed "To many requests" in testing).
   */
  getWarehouses: async (apiKey: string, cityRef: string): Promise<WarehouseSearchResult[]> => {
    const response = await call(apiKey, 'Address', 'getWarehouses', {
      CityRef: cityRef,
      Limit: '500',
    });
    if (!response.success) {
      firstErrorOrThrow(response, 'Не вдалося отримати список відділень');
    }
    return response.data.map((w) => ({
      ref: w.Ref as string,
      number: w.Number as string,
      description: w.Description as string,
      category: w.CategoryOfWarehouse as string,
    }));
  },

  /**
   * Address/getCities filtered by Ref — resolves an already-saved `defaultSenderCityRef`
   * (a DeliveryCity GUID) back to its display name, e.g. when opening the edit-sender dialog.
   * `Address/getCities`'s own `Ref` field is the same DeliveryCity identifier space returned
   * by `searchSettlements`, so round-trips correctly. Returns null if the city no longer exists.
   */
  getCityByRef: async (apiKey: string, cityRef: string): Promise<CitySearchResult | null> => {
    const response = await call(apiKey, 'Address', 'getCities', { Ref: cityRef });
    if (!response.success || response.data.length === 0) {
      return null;
    }
    const city = response.data[0];
    return { ref: city.Ref as string, present: city.Description as string };
  },

  /**
   * Address/getWarehouses filtered by Ref — same reverse-lookup purpose as getCityByRef,
   * for an already-saved `defaultSenderWarehouseRef`.
   */
  getWarehouseByRef: async (apiKey: string, warehouseRef: string): Promise<WarehouseSearchResult | null> => {
    const response = await call(apiKey, 'Address', 'getWarehouses', { Ref: warehouseRef });
    if (!response.success || response.data.length === 0) {
      return null;
    }
    const warehouse = response.data[0];
    return {
      ref: warehouse.Ref as string,
      number: warehouse.Number as string,
      description: warehouse.Description as string,
      category: warehouse.CategoryOfWarehouse as string,
    };
  },

  /**
   * Counterparty/getCounterparties (type Sender) — the company's own sender identity in
   * Nova Poshta, same call already used to validate the API key (Фаза 1).
   */
  getSenderCounterpartyRef,

  /**
   * Counterparty/getCounterpartyContactPersons — a Counterparty (Sender or Recipient) can
   * have several ContactPersons (different people), each with their own phone — the one a
   * courier calls. Selected per shipment for the sender (not a stored company default);
   * also used internally to resolve a freshly created recipient's ContactPerson Ref.
   */
  getContactPersons,
};
